import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { useLazyListDriverQuery } from "store/services/driverApi";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { BaseTabs } from "components/Shared/BaseTabs";
import BaseSelect from "components/Shared/Base/BaseSelect";
import ExportDriverExcel from "./driverComponents/ExportDriverExcel";
import { useAppMeta, useTablePagination } from "hooks";

const DriverListScreen = () => {
  const [sortOrder, setSortOrder] = React.useState("createdAt:-1");
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const breadcrumbs = [{ title: "Drivers" }];
  const driverListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Driver ID",
      selector: (row) => row?.driverId,
      cell: (row) => (
        <Link
          title={row?.driverId}
          to={`/app/crm/driver/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.driverId ? (
            <HighlightedText text={row?.driverId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <Link
          title={row?.name}
          to={`/app/crm/driver/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.name ? (
            <HighlightedText text={row?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Email",
      selector: (row) => row?.email,
      cell: (row) => (
        <a
          href={`mailto:${row?.email}`}
          className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.email ? (
            <HighlightedText text={row?.email} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </a>
      ),
    },
    {
      name: "Phone",
      selector: (row) => row?.phone,
      cell: (row) => row?.phone || "N/A",
    },
    {
      name: "Vehicle ID",
      selector: (row) =>
        row.vehicles && row.vehicles.length > 0
          ? row.vehicles.map((v) => v.registration_number).join(", ")
          : "N/A",
      cell: (row) => {
        if (row.vehicles && row.vehicles.length > 0) {
          const vehicleNumbers = row.vehicles
            .map((v) => v.registration_number)
            .filter(Boolean);
          if (vehicleNumbers.length > 2) {
            return (
              <span>
                {vehicleNumbers.slice(0, 2).join(", ") + ", ..."}
              </span>
            );
          }
          return <span>{vehicleNumbers.join(", ")}</span>;
        }
        return <span>N/A</span>;
      },
    },
    {
      name: "License ID",
      cell: (row) => {
        const licences = Array.isArray(row?.driverLicence)
          ? row.driverLicence
          : row?.driverLicence
          ? [row.driverLicence]
          : [];
        const activeLicenceIds = licences
          .filter((licence) => licence?.isActive)
          .map((licence) => licence?.licenceIdd)
          .filter(Boolean);

        return activeLicenceIds.length > 2
          ? activeLicenceIds.slice(0, 2).join(", ") + ", ..."
          : activeLicenceIds.join(", ") || "N/A";
      },
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/driver/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Driver" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/driver/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Driver">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchDrivers, { data: driverRes, isLoading, isFetching, error }] =
    useLazyListDriverQuery();

  React.useEffect(() => {
    const [sortedBy, sortDirection] = sortOrder.split(":");
    fetchDrivers({
      Active: activeTab !== "Inactive",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      sortedBy,
      sortDirection: parseInt(sortDirection),
    });
  }, [activeTab, currentPage, fetchDrivers, keyword, perPage, sortOrder]);

  useAppMeta(`${activeTab} Drivers`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={driverListTabs}
          defaultValue={activeTab}
          actions={<ExportDriverExcel activeTab={activeTab} />}
        />
        <TableFilter
          searchLabel="Name/Display Name/Email"
          disabled={isLoading || isFetching}
          initialFilters={2}
        >
          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            options={[
              { label: "CreatedAt (Desc)", value: "createdAt:-1" },
              { label: "CreatedAt (Asc)", value: "createdAt:1" },
              { label: "UpdatedAt (Desc)", value: "updatedAt:-1" },
              { label: "UpdatedAt (Asc)", value: "updatedAt:1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={driverRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={driverRes?.totalItems || 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DriverListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/driver/create">
        <Button startIcon={<Add />} variant="contained">
          New Driver
        </Button>
      </Link>
    </>
  );
};
